import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Output,
  computed,
  input,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import BigNumber from 'bignumber.js';
import { DocumentEventService } from 'gain-lib/services/documentClickDetectionService.service';
import { GainMoneyModule } from 'gain-web/shared-modules/money/money.module';
import { Subscription } from 'rxjs';

export type ValueType = 'percent' | 'number' | 'string';

type AlignmentBehavior = 'left' | 'right';
type AlignmentBehaviorOptions = 'auto' | AlignmentBehavior;

@Component({
  selector: 'gax-table-cell-value-edit',
  templateUrl: './table-cell-value-edit.component.html',
  styleUrl: './table-cell-value-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    GainMoneyModule,
  ],
})
export class TableCellValueEditComponent {
  valueType = input<ValueType>('string');
  value = model.required<string | number | null>();
  protected inputValue = signal<string | number | null>(null);
  precisionDigits = input<number>(2);
  min = input<number | null>();
  max = input<number | null>();
  icon = input<string | null>();
  align = input<AlignmentBehaviorOptions>('auto');
  disabled = input<boolean>(false);
  duplicateValidationList = input<string[] | null>();
  required = input<boolean>(false);

  @HostBinding('class.edited')
  edited?: boolean | undefined | null;

  placeholder = input<string>('');

  @Output()
  erroredValue = new EventEmitter<string>();
  @Output()
  triggerExitMode = new EventEmitter<boolean>();

  @HostBinding('class.left-align')
  get leftAlign() {
    return this.alignmentBehavior === 'left';
  }

  get alignmentBehavior(): AlignmentBehavior {
    const option = this.align();
    if (option === 'auto') {
      return this.valueType() === 'number' ? 'right' : 'left';
    }
    return option;
  }

  protected get hasCustomIcon() {
    return this.icon() != null;
  }

  protected userEditableValue = computed(() => {
    const value = this.value();
    if (this.valueType() === 'percent' && value != null) {
      return new BigNumber(value).times(100).toNumber();
    }
    return value ?? '';
  });

  editing = signal<boolean>(false);

  @HostBinding('class.editing')
  get isEditing() {
    return this.editing();
  }

  _isClean = signal<boolean>(true);

  @HostBinding('class.clean')
  get isClean(): boolean {
    return this._isClean();
  }

  documentClickSubscription!: Subscription;

  computedErrorsTooltip = computed(() => {
    if (this._isClean()) {
      return null;
    }

    const errors = this.checkForErrors(null);
    return errors.length > 0 ? errors.join(', ') : null;
  });

  constructor(
    private elRef: ElementRef,
    private documentEventService: DocumentEventService,
    private cd: ChangeDetectorRef,
  ) {
    this.documentClickSubscription = this.documentEventService.click$
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        this.documentClickListener(event.target as HTMLElement);
      });
  }

  documentClickListener(target: HTMLElement | undefined): void {
    if (target && this.elRef.nativeElement.contains(target)) {
      // clicked inside
    } else {
      this.exitEditMode();
    }
  }

  onInputChange(inputChangeEvent: Event) {
    this._isClean.set(false);
    const target = inputChangeEvent.target as HTMLInputElement;
    const value = target.value;
    let updatedValue = '';

    if (this.valueType() === 'number') {
      if (target.value !== null && target.value.toString().length > 17) {
        if (
          target.value.toString().includes('.') &&
          target.value.toString().split('.')[1].length > 0 &&
          target.value.toString().split('.')[1].length < 10
        ) {
          const decimalIndex = value.indexOf('.');
          if (decimalIndex !== -1) {
            const wholeNumber = value.slice(0, decimalIndex);
            const fracNumber = value.slice(decimalIndex + 1);
            updatedValue =
              wholeNumber + '.' + fracNumber.slice(0, fracNumber.length - 1);
            target.value = updatedValue;
          }
        }
      }

      if (
        target.value !== null &&
        target.value.toString().includes('.') &&
        target.value.toString().split('.')[1].length > 10
      ) {
        const decimalIndex = value.indexOf('.');
        if (decimalIndex !== -1) {
          const wholeNumber = value.slice(0, decimalIndex);
          const fracNumber = value.slice(decimalIndex + 1);
          updatedValue = wholeNumber + '.' + fracNumber.slice(0, 10);
          target.value = updatedValue;
        }
      }

      if (
        target.value !== null &&
        target.value.toString().length > 17 &&
        !target.value.toString().includes('.')
      ) {
        const wholeNumber = value.slice(0, 17);
        updatedValue = wholeNumber;
        target.value = wholeNumber;
      }

      const numberValue = parseFloat(
        updatedValue === '' ? value : updatedValue,
      );
      if (Number.isNaN(numberValue)) {
        this.inputValue.set(null);
        return;
      }
      this.inputValue.set(numberValue);
    }

    if (this.valueType() === 'percent') {
      const numberValue = parseFloat(value);
      if (Number.isNaN(numberValue)) {
        this.inputValue.set(null);
        return;
      }
      this.inputValue.set(numberValue);
    }

    if (this.valueType() === 'string') {
      this.inputValue.set(value === '' ? null : value);
      return;
    }
  }

  enterEditMode(element: HTMLDivElement) {
    this.editing.update(() => true);
    setTimeout(() => {
      const inputBox = element.querySelector('input');
      if (inputBox) {
        inputBox.focus();
      }
      this.cd.detectChanges();
      this.cd.markForCheck();
    }, 100);
  }

  exitEditMode(clearErrors = false) {
    if (clearErrors) {
      this._isClean.set(true);
    }

    this.editing.update(() => false);
    this.cd.detectChanges();
    this.cd.markForCheck();
    this.triggerExitMode.emit();
  }

  updateValue(newValue: string, event: Event): void {
    let value: string | number | null = newValue as string | number | null;

    event.stopPropagation();

    const validationErrors = this.checkForErrors(value);
    if (validationErrors.length > 0) {
      this.erroredValue.emit();
      return;
    }

    if (this.valueType() !== 'string') {
      value = parseFloat(newValue);
      if (this.valueType() === 'percent') {
        value = new BigNumber(value).dividedBy(100).toNumber();
      }
    }

    this.inputValue.set(value);
    this.value.set(value);
    this.edited = true;
    this.exitEditMode();
  }

  checkForErrors(newValue: string | number | null): string[] {
    const errors: string[] = [];
    const value = this.inputValue() ?? newValue;

    const isRequired = this.required();
    const min = this.min();
    const max = this.max();
    const duplicateValidationList = this.duplicateValidationList() || [];

    if (value === null || value === undefined || value === '') {
      if (isRequired) {
        errors.push('Value required');
      }
    } else if (this.valueType() === 'string') {
      if (value.toString().trim() === '') {
        errors.push('Value required');
      }

      if (value !== value.toString().trim()) {
        errors.push('No trailing space allowed');
      }
    } else {
      const numberValue = parseFloat(value as string);

      if (typeof min === 'number' && numberValue < min) {
        errors.push('Value must be equal or greater than ' + min);
      }

      if (typeof max === 'number' && numberValue > max) {
        errors.push('Value must be less than ' + max);
      }
    }

    if (duplicateValidationList.length) {
      const isDuplicate = duplicateValidationList.some(
        (item) => item.toLowerCase() === value?.toString().toLowerCase(),
      );
      if (isDuplicate) {
        errors.push('The value provided is identical to another entry');
      }
    }

    if (errors.length > 0) {
      this.erroredValue.emit();
    }

    return errors;
  }
}
